import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { isIos, isStandalone } from './usePwaInstall';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// VAPID keys are base64url; PushManager wants a Uint8Array.
const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
};

export type PushSupport =
  | 'supported'
  | 'unsupported'
  | 'needs-install'; // iOS: Push API only exists once added to the Home Screen

export function usePushNotifications() {
  const [support, setSupport] = useState<PushSupport>('unsupported');
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hasApis =
      'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';

    if (hasApis) {
      setSupport('supported');
    } else if (isIos() && !isStandalone()) {
      // Safari exposes no PushManager in a tab — installing is the only route.
      setSupport('needs-install');
    } else {
      setSupport('unsupported');
    }

    if (!hasApis) return;
    // getRegistration resolves with undefined when nothing is registered;
    // serviceWorker.ready would hang forever instead.
    navigator.serviceWorker
      .getRegistration()
      .then((reg) => reg?.pushManager.getSubscription())
      .then((sub) => setSubscribed(Boolean(sub)))
      .catch(() => setSubscribed(false));
  }, []);

  const subscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      // iOS requires this to originate from a user gesture — the caller is a click.
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        setError(
          result === 'denied'
            ? 'Notifications are blocked. Enable them for this app in your device settings.'
            : 'Notification permission was dismissed.'
        );
        return false;
      }

      // Fail fast rather than hanging on serviceWorker.ready when no worker is
      // registered — which is the case in development, where it's disabled.
      const existing = await navigator.serviceWorker.getRegistration();
      if (!existing) {
        setError(
          'No service worker is registered, so push can\'t be enabled. In development this is expected — test against a production build.'
        );
        return false;
      }

      const { data } = await axios.get(`${API_BASE_URL}/api/notifications/vapid-public-key`);
      const registration = await navigator.serviceWorker.ready;

      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true, // required; silent push isn't allowed
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        }));

      await axios.post(`${API_BASE_URL}/api/notifications/subscribe-push`, {
        subscription: subscription.toJSON(),
      });

      setSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push subscribe failed', err);
      setError('Could not enable push notifications. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await axios.post(`${API_BASE_URL}/api/notifications/unsubscribe-push`, {
          endpoint: subscription.endpoint,
        });
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      return true;
    } catch (err) {
      console.error('Push unsubscribe failed', err);
      setError('Could not turn off push notifications.');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  return { support, permission, subscribed, busy, error, subscribe, unsubscribe };
}
